import { forwardRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { quantityActionPropType } from "../../../propTypes";

/**
 * Specialized quantity control button for increment/decrement actions
 * Used in cart items, product pages, etc.
 *
 * @component
 * @param {Object} props
 * @param {('increment'|'decrement')} props.action - Increment (+) or decrement (-)
 * @param {Function} props.onClick - Click handler
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {string} [props.ariaLabel] - Accessibility label (auto-generated if not provided)
 * @param {string} [props.className] - Additional CSS classes
 */
const QuantityButton = forwardRef(({ action, onClick, disabled = false, ariaLabel, className, ...rest }, ref) => {
  const isIncrement = action === "increment";
  const defaultAriaLabel = isIncrement ? "Increase quantity" : "Decrease quantity";

  const buttonClasses = clsx(
    "qty-btn",
    {
      "qty-btn-plus": isIncrement,
      "qty-btn-minus": !isIncrement,
    },
    className
  );

  return (
    <button
      ref={ref}
      type="button"
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || defaultAriaLabel}
      {...rest}
    >
      {isIncrement ? "+" : "−"}
    </button>
  );
});

QuantityButton.displayName = "QuantityButton";

QuantityButton.propTypes = {
  action: quantityActionPropType.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

export default QuantityButton;
